import { useMemo } from 'react';

import type {
  GetSitrepsResponse,
  SitrepViewDataProps,
} from 'components/Reports/reports.types';

export type ReportsScope = {
  incidentId: UUID | null;
  aorId: UUID | null;
  assetId?: UUID | null;
  customReportId?: UUID | null;
};

type ScopedSitrepViewData = {
  publishedSitreps: SitrepViewDataProps;
  draftSitreps: SitrepViewDataProps;
  pendingReviews: SitrepViewDataProps;
};

type UseScopedSitrepViewDataArgs = ScopedSitrepViewData & {
  scope: ReportsScope;
};

const isAreaSubject = (sitrep: GetSitrepsResponse) =>
  sitrep.subject_kind === 'asset' || sitrep.subject_kind === 'custom';

export const filterSitrepsByScope = (
  sitreps: GetSitrepsResponse[],
  { incidentId, aorId, assetId, customReportId }: ReportsScope
) => {
  const sitrepOnly = sitreps.filter((sitrep) => sitrep.is_sitrep);

  if (customReportId) {
    return sitrepOnly.filter((sitrep) => sitrep.id === customReportId);
  }

  if (assetId) {
    return sitrepOnly.filter(
      (sitrep) => sitrep.subject_kind === 'asset' && sitrep.asset_id === assetId
    );
  }

  if (incidentId) {
    return sitrepOnly.filter((sitrep) => sitrep.incident_id === incidentId);
  }

  if (aorId) {
    return sitrepOnly.filter(
      (sitrep) =>
        sitrep.aor_id === aorId &&
        sitrep.incident_id === null &&
        !isAreaSubject(sitrep)
    );
  }

  return sitrepOnly;
};

const scopeSitrepViewData = (
  sitrepViewData: SitrepViewDataProps,
  scope: ReportsScope
): SitrepViewDataProps => ({
  ...sitrepViewData,
  data: filterSitrepsByScope(sitrepViewData.data, scope),
});

export const useScopedSitrepViewData = ({
  publishedSitreps,
  draftSitreps,
  pendingReviews,
  scope,
}: UseScopedSitrepViewDataArgs): ScopedSitrepViewData => {
  return useMemo(
    () => ({
      publishedSitreps: scopeSitrepViewData(publishedSitreps, scope),
      draftSitreps: scopeSitrepViewData(draftSitreps, scope),
      pendingReviews: scopeSitrepViewData(pendingReviews, scope),
    }),
    [draftSitreps, pendingReviews, publishedSitreps, scope]
  );
};
